import type { PolygonWithHoles, Vec2 } from '../../geo/types';
import { triangulate } from '../../geo/triangulate';
import { ringBounds, signedArea } from '../../geo/polygon';
import { withoutDegenerates } from './degenerate';
import { withBoundaryStations } from '../mesh/boundaryTriangulation';

/** Street tiles and coarser foundation tiles share these boundary stations. */
const BOUNDARY_GRID_M = 8;

function boundaryStations(ring: readonly Vec2[]): readonly Vec2[] {
  const out: Vec2[] = [];
  let added = false;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!,
      b = ring[(i + 1) % ring.length]!,
      shares: number[] = [];
    out.push(a);
    for (const axis of [0, 1] as const) {
      const delta = b[axis] - a[axis];
      if (delta === 0) continue;
      const lo = Math.min(a[axis], b[axis]),
        hi = Math.max(a[axis], b[axis]);
      for (
        let at = Math.floor(lo / BOUNDARY_GRID_M) * BOUNDARY_GRID_M + BOUNDARY_GRID_M;
        at < hi;
        at += BOUNDARY_GRID_M
      ) {
        const share = (at - a[axis]) / delta;
        if (share > 1e-12 && share < 1 - 1e-12) shares.push(share);
      }
    }
    shares.sort((a, b) => a - b);
    let previous = -Infinity;
    for (const share of shares) {
      if (share - previous < 1e-12) continue;
      out.push([a[0] + (b[0] - a[0]) * share, a[1] + (b[1] - a[1]) * share]);
      previous = share;
      added = true;
    }
  }
  return added ? out : ring;
}

function stationed(polygon: PolygonWithHoles): PolygonWithHoles {
  const outer = boundaryStations(polygon.outer),
    holes = polygon.holes.map(boundaryStations);
  return outer === polygon.outer && holes.every((h, i) => h === polygon.holes[i])
    ? polygon
    : { outer, holes };
}

function clip(points: readonly Vec2[], axis: 0 | 1, bound: number, above: boolean): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!,
      b = points[(i + 1) % points.length]!;
    const inA = above ? a[axis] >= bound : a[axis] <= bound;
    const inB = above ? b[axis] >= bound : b[axis] <= bound;
    if (inA) out.push(a);
    if (inA !== inB) {
      const share = (bound - a[axis]) / (b[axis] - a[axis]);
      const p: [number, number] = [a[0] + (b[0] - a[0]) * share, a[1] + (b[1] - a[1]) * share];
      p[axis] = bound;
      out.push(p);
    }
  }
  return out;
}

/** Tile the polygon's convex triangles, whose rectangle clips remain convex even around holes.
 * Rows precede columns so a winding street pays for cells it touches, not its whole plan box.
 * This avoids repeated polygon boolean operations in the bounded height-mesh build jobs. */
export function heightTiles(polygon: PolygonWithHoles, size: number): readonly PolygonWithHoles[] {
  const box = ringBounds(polygon.outer);
  if (box.maxX - box.minX <= size && box.maxY - box.minY <= size) return [stationed(polygon)];
  // Mend needle ears before distributing their neighbours among independent tiles. Restore the
  // real outline stations in those neighbours, rather than leaving a discarded ear as a gap.
  const tri = withBoundaryStations(withoutDegenerates(triangulate(polygon)), polygon);
  const pieces: PolygonWithHoles[] = [];
  for (let t = 0; t < tri.indices.length; t += 3) {
    const points = [0, 1, 2].map((k): Vec2 => {
      const v = tri.indices[t + k]!;
      return [tri.positions[v * 2]!, tri.positions[v * 2 + 1]!];
    });
    const bound = ringBounds(points);
    for (let y = Math.floor(bound.minY / size) * size; y < bound.maxY; y += size) {
      const row = clip(clip(points, 1, y, true), 1, y + size, false);
      if (row.length < 3) continue;
      const span = ringBounds(row);
      for (let x = Math.floor(span.minX / size) * size; x < span.maxX; x += size) {
        const outer = clip(clip(row, 0, x, true), 0, x + size, false);
        if (outer.length >= 3 && Math.abs(signedArea(outer)) > 1e-8)
          pieces.push(stationed({ outer, holes: [] }));
      }
    }
  }
  return pieces;
}
